import { useCallback, useEffect, useState } from 'react';

import { profileRepository } from '@/repositories/profile-repository';
import type { UserProfileDto } from '@/types/social';
import { useAuthToken, useCurrentConnection } from '@/hooks/auth';

export function useProfile() {
	const [profile, setProfile] = useState<UserProfileDto | null>(null);
	const { account } = useCurrentConnection();
	const { jwtToken } = useAuthToken();
	const address = account?.address;

	const refreshProfile = useCallback(async () => {
		if (!address) {
			setProfile(null);
			return null;
		}

		if (!jwtToken) {
			return null;
		}

		try {
			const bundle = await profileRepository.getProfile(address);
			setProfile(bundle);
			return bundle;
		} catch (error) {
			const status = (error as { response?: { status?: number } } | null)?.response?.status;
			if (status === 401) {
				setProfile(null);
				return null;
			}
			throw error;
		}
	}, [address, jwtToken]);

	useEffect(() => {
		if (!address || !jwtToken) {
			return;
		}

		void refreshProfile();
	}, [address, jwtToken, refreshProfile]);

	const clearProfile = useCallback(() => {
		setProfile(null);
	}, []);

	return {
		profile,
		refreshProfile,
		clearProfile,
	};
}
